/** Bidirectional mapping between the app's primary navigation state and the URL
 * hash, so a document / node / tab is shareable + bookmarkable and the browser
 * Back/Forward buttons move between views. Hash-based (no server routing) to fit
 * the static PWA + vite proxy. Pure functions — unit-tested in url-state.test.ts. */
export interface RouteState {
  tab: "graph" | "orbits" | "reader" | "ask" | "doc";
  /** reader: the document being read */
  docId?: string;
  /** graph: the selected node (deep-linkable focus target) */
  nodeId?: string;
  /** doc: which store the id belongs to — a reader document, or a markdown node. */
  docKind?: "document" | "node";
}

/**
 * `#/doc?id=…&kind=…` — THE addressable form of a single note.
 *
 * `#/reader?doc=…` could only ever name a reader document; a markdown note had
 * no URL at all, which is why `useDeepLink` used to skip markdown panels
 * outright. That gap is not just a missing feature: **every page needs a stable
 * address before real-time collaboration and sharing land.** A shared cursor, a
 * "send someone this note" link and a presence indicator all need something to
 * point at, and retrofitting addressability after those exist is far more
 * expensive than having it already.
 *
 * `#/reader?doc=…` still parses, so old links and bookmarks keep working.
 */
export function noteHref(id: string, kind: "document" | "node"): string {
  return encodeRoute({ tab: "doc", docId: id, docKind: kind });
}

export function encodeRoute(state: RouteState): string {
  const params = new URLSearchParams();
  if (state.tab === "reader" && state.docId) params.set("doc", state.docId);
  if (state.tab === "doc" && state.docId) {
    params.set("id", state.docId);
    params.set("kind", state.docKind ?? "document");
  }
  if (state.nodeId) params.set("node", state.nodeId);
  const query = params.toString();
  return `#/${state.tab}${query ? `?${query}` : ""}`;
}

export function parseRoute(hash: string): RouteState | null {
  const match = /^#\/(graph|orbits|reader|ask|doc)(?:\?(.*))?$/.exec(hash);
  if (!match) return null;
  const tab = match[1] as RouteState["tab"];
  const params = new URLSearchParams(match[2] ?? "");
  const nodeId = params.get("node") ?? undefined;
  if (tab === "doc") {
    const id = params.get("id");
    if (!id) return null; // a doc route without a target is not a route
    const kind = params.get("kind") === "node" ? "node" : "document";
    return { tab, docId: id, docKind: kind, nodeId };
  }
  const docId = params.get("doc") ?? undefined;
  if (tab === "reader" && !docId) return null; // a reader route needs a doc
  return { tab, docId, nodeId };
}

/** True when the location hash is a Supabase auth-callback fragment rather than an
 * app route. Supabase uses the IMPLICIT flow (its default), so an OAuth sign-in,
 * an email-verification click, and a password-recovery link all return to the app
 * as `#access_token=…&…&type=signup|recovery` (or `#error=…` on failure).
 * supabase-js's `detectSessionInUrl` consumes and strips that fragment
 * ASYNCHRONOUSLY once the SDK chunk loads — which is how sign-in completes. The
 * hash router must therefore NEVER overwrite such a fragment on mount; doing so
 * dropped the freshly-minted session and bounced the user back to the sign-in
 * splash (the "impossible to log in" bug). */
export function isAuthCallbackHash(hash: string): boolean {
  if (!hash || hash === "#") return false;
  // Supabase writes `#key=val&…` (no leading slash); app routes are `#/tab…`.
  const params = new URLSearchParams(hash.replace(/^#\/?/, ""));
  const type = params.get("type");
  return (
    params.has("access_token") ||
    params.has("refresh_token") ||
    params.has("provider_token") ||
    params.has("error") ||
    params.has("error_description") ||
    type === "recovery" ||
    type === "signup" ||
    type === "magiclink" ||
    type === "invite" ||
    type === "email_change"
  );
}
